import { EnvironmentType } from "@app-context/environments/context";
import { APISource } from "@app-context/networkCalls/types";
import { ValueOf } from "@enfusion-ui/types";
import * as React from "react";

export const RequestTypes = {
  HTTP: "http",
  ENFUSION: "rest",
  REPORT: "report",
  OEMS: "oems",
} as const;

export type HTTPMethod =
  | "GET"
  | "POST"
  | "PUT"
  | "DELETE"
  | "HEAD"
  | "OPTIONS"
  | "PATCH";

export type Predicate = "Any" | "All";
export type GroupPredicate = "And" | "Or";
export type RequestType = ValueOf<typeof RequestTypes>;

export type PrefilterExpression = {
  columnIdentifier: string;
  operator: string;
  operand1: Object;
  operand2: Object | null;
};

export type UserDetails = {
  username: string;
  password: string;
  adminuser: string;
  adminpassword: string;
  inputToken: string;
};

export type InspectorHTTPConfig = {
  method: HTTPMethod;
  path: string;
  body: string;
  params: Record<string, string>;
  headers: Record<string, string>;
} & UserDetails;

export type InspectorRestConfig = {
  method: HTTPMethod;
  path: string;
  body: string;
  params: Record<string, string>;
  headers: Record<string, string>;
  prefilterExpressions: Array<PrefilterExpression>;
  predicate: Predicate;
  groupPredicate: GroupPredicate;
} & UserDetails;

export type InspectorReportConfig = {
  path: string;
  prefilterExpressions: Array<PrefilterExpression>;
  predicate: Predicate;
  groupPredicate: GroupPredicate;
  params: Record<string, string>;
  env: string;
} & UserDetails;

export type InspectorOEMSConfig = {
  path: string;
  env: string;
} & UserDetails;

export type InspectorContextConfig =
  | InspectorHTTPConfig
  | InspectorRestConfig
  | InspectorReportConfig
  | InspectorOEMSConfig;

export type InspectorContextValues = {
  requestType: RequestType;
  apiSource: APISource;
  environment: EnvironmentType;
  config: InspectorContextConfig;
  requiresTwoFactorAuthentication: boolean;
};

export type InspectorContextState = InspectorContextValues & {
  setConfig: (config: InspectorContextConfig) => void;
  handleTypeChange: (value: RequestType | null) => void;
  authenticateWith2FA: () => Promise<void>;
  getSecureToken: () => Promise<string>;
  getOEMSSessionToken: () => Promise<string>;
  handleAPISourceChange: (value: APISource | null) => void;
  handleEnvironmentChange: (value: EnvironmentType | null) => void;
};

export const DEFAULT_CONFIG = {
  http: {
    method: "GET",
    path: "",
    params: {},
    body: "",
    headers: {},
  } as InspectorHTTPConfig,
  rest: {
    method: "GET",
    path: "",
    body: "",
    headers: {},
    username: "",
    password: "",
    adminuser: "",
    adminpassword: "",
    inputToken: "",
    params: {},
    prefilterExpressions: [] as Array<PrefilterExpression>,
  } as InspectorRestConfig,
  oems: {
    username: "",
    password: "",
    adminuser: "",
    adminpassword: "",
    inputToken: "",
    path: "",
  } as InspectorOEMSConfig,
  report: {
    path: "",
    username: "",
    password: "",
    adminuser: "",
    adminpassword: "",
    inputToken: "",
  } as InspectorReportConfig,
} as const;

export const InspectorContext = React.createContext<
  InspectorContextState | undefined
>(undefined);

export const useInspectorContext = () => {
  const context = React.useContext<InspectorContextState | undefined>(
    InspectorContext
  );
  if (context === undefined) {
    throw new Error(
      "useInspectorContext must be used within a InspectorContext.Provider"
    );
  }
  return context;
};
